import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Label,
  Input,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import AppointmentService from "../../../services/AppointmentServices/AppointmentService";
import toast from "react-hot-toast";

const EditDateTime = ({
  isOpen,
  toggle,
  event,
  dataList,
  onStatusChange,
  fetchAppointmentDetails,
  toggleRightCanvas,
  setEvent,
}) => {
  console.log("🚀 ~ event:", event)
  const [customDuration, setCustomDuration] = useState("");
  const [price, setPrice] = useState(event?.appointment?.total_amount);
  const [startTime, setStartTime] = useState(event?.start || "");
  const [endTime, setEndTime] = useState(event?.end || "");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Function to calculate duration between two times
  const calculateDuration = (start, end) => {
    if (!start || !end) return "00:00";

    const startDate = new Date(start);
    const endDate = new Date(end);

    const diff = endDate - startDate;
    if (diff < 0) return "00:00";

    const totalMinutes = Math.floor(diff / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
  };

  // Function to calculate end time based on duration
  const calculateEndTime = (start, duration) => {
    if (!start || !duration) return "";

    const [hours, minutes] = duration.split(":").map(Number);
    const startDate = new Date(start);

    const endDate = new Date(
      startDate.getTime() + (hours * 60 + minutes) * 60000
    );
    return endDate;
  };

  // Automatically calculate duration when `startTime` and `endTime` are available
  useEffect(() => {
    if (startTime && endTime) {
      setCustomDuration(calculateDuration(startTime, endTime));
    }
  }, [startTime, endTime]);

  // Handle start time change
  const handleStartTimeChange = ([date]) => {
    setStartTime(date);
    setErrors((prev) => ({ ...prev, startTime: "" }));

    if (date && customDuration) {
      setEndTime(calculateEndTime(date, customDuration));
    } else if (endTime) {
      setCustomDuration(calculateDuration(date, endTime));
    }
  };

  // Handle end time change
  const handleEndTimeChange = ([date]) => {
    setEndTime(date);
    setErrors((prev) => ({ ...prev, endTime: "" }));

    if (startTime) {
      setCustomDuration(calculateDuration(startTime, date));
    }
  };
  const validateDuration = (duration) => {
    // Validate the format "HH:mm"
    const regex = /^\d{2}:\d{2}$/;
    if (!regex.test(duration)) return false;

    const [hours, minutes] = duration.split(":").map(Number);
    return hours >= 0 && minutes >= 0 && minutes < 60; // Validate hours and minutes
  };

  // Handle duration change
  const handleDurationChange = (e) => {
    const newDuration = e.target.value;
    const isValid = validateDuration(newDuration);

    setCustomDuration(newDuration);
    if (isValid) {
      setErrors((prev) => ({ ...prev, customDuration: "" }));

      if (startTime) {
        const newEndTime = calculateEndTime(startTime, newDuration);
        setEndTime(newEndTime);
      } else if (endTime) {
        const newStartTime = calculateStartTime(endTime, newDuration);
        setStartTime(newStartTime);
      }
    } else {
      setErrors((prev) => ({
        ...prev,
        customDuration: "Invalid duration format (HH:mm)",
      }));
    }
  };

  const formatDateTime = (date) => {
    return (
      date.getFullYear() +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getDate()).padStart(2, "0") +
      " " +
      String(date.getHours()).padStart(2, "0") +
      ":" +
      String(date.getMinutes()).padStart(2, "0") +
      ":" +
      String(date.getSeconds()).padStart(2, "0")
    );
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      const formattedStartTime = formatDateTime(new Date(startTime));
      const formattedEndTime = formatDateTime(new Date(endTime));

      const response = await AppointmentService.editAppointment(
        dataList?.sessions[0]?.id,
        {
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          duration: customDuration,
          base_price: price,
        }
      );
      toast.success("The appointment date is updated successfully.");
      // setDataList(response.data);

      fetchAppointmentDetails();
      toggle();
      onStatusChange();
      toggleRightCanvas();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setStartTime(event?.start || "");
      setEndTime(event?.end || "");

      // Recalculate duration when resetting
      const duration = calculateDuration(event?.start, event?.end);
      setCustomDuration(duration);

      setErrors({});
    }
  }, [isOpen, event]);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Edit Appointment Date and Time</ModalHeader>
      <ModalBody>
        <Row>
          {/* Start Time */}
          <Col md={6} xs={12} className="mb-2">
            <Label>Start Time</Label>
            <Flatpickr
              placeholder="Select a start time"
              className={`form-control ${errors.startTime ? "is-invalid" : ""}`}
              value={startTime}
              onChange={handleStartTimeChange}
              options={{
                enableTime: true,
                altInput: true,
                noCalendar: false,
                dateFormat: "d/m/Y H:i",
                altFormat: "d/m/Y H:i",
                time_24hr: false,
                disableMobile: true,
              }}
            />
            {errors.startTime && (
              <div className="text-danger">{errors.startTime}</div>
            )}
          </Col>

          {/* Duration */}
          <Col md={6} xs={12} className="mb-2">
            <Label>Duration</Label>
            <input
              type="text"
              placeholder="Enter duration (HH:mm)"
              className={`form-control ${
                errors.customDuration ? "is-invalid" : ""
              }`}
              value={customDuration}
              onChange={handleDurationChange}
            />
            {errors.customDuration && (
              <div className="text-danger">{errors.customDuration}</div>
            )}
          </Col>

          {/* End Time */}
          <Col md={6} xs={12} className="mb-2">
            <Label>End Time</Label>
            <Flatpickr
              placeholder="Select an end time"
              className={`form-control ${errors.endTime ? "is-invalid" : ""}`}
              value={endTime}
              onChange={handleEndTimeChange}
              options={{
                enableTime: true,
                altInput: true,
                noCalendar: false,
                dateFormat: "d/m/Y H:i",
                altFormat: "d/m/Y H:i",
                time_24hr: false,
                disableMobile: true,
              }}
            />
            {errors.endTime && (
              <div className="text-danger">{errors.endTime}</div>
            )}
          </Col>
          <Col md={6} xs={12}>
            <Label>Price</Label>
            <Input
              name="price"
              id="price"
              type="text"
              placeholder="Price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Col>
        </Row>
        <div className="d-flex justify-content-end gap-3">
          <button className="btn btn-danger" type="button" onClick={toggle}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            type="submit"
            onClick={handleEdit}
            disabled={loading}
          >
            Update
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default EditDateTime;
